import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BiEdit, BiPlus, BiTrash } from 'react-icons/bi';

import Bouton from '../../UI/Bouton/Bouton';
import DataGrid from '../../UI/DataGrid/DataGrid';

import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import S from './TableauTasks.module.scss';

const COLUMN_CONFIG = [
  ['task_id', 'ID', 120],
  ['name', 'Tache', 160],
  ['function_name', 'Fonction', 180],
  ['type', 'Mode', 140],
  ['state', 'Active', 120],
  ['frequency', 'Frequence/Date', 220],
  ['actions', 'Actions', 180],
];

const createColumn = (field, label, minWidth) => {
  const baseColumn = {
    field,
    headerName: label,
    flex: 1,
    minWidth,
    sortable: field !== 'actions' && field !== 'frequency',
    align: 'center',
    headerAlign: 'center',
  };

  if (field === 'frequency') {
    return {
      ...baseColumn,
      sortable: false,
      renderCell: (params) => <span>{params.value}</span>,
    };
  }

  if (field === 'actions') {
    return {
      ...baseColumn,
      sortable: false,
      renderCell: (params) => params.value,
    };
  }

  return baseColumn;
};

const TableauTasks = ({
  taskSelectionnee,
  editerTache,
  creerTache,
  supprimerTache,
  tasks,
}) => {
  const [filtreNom, setFiltreNom] = useState('');
  const [filtreId, setFiltreId] = useState('');
  const [tachesFiltrees, setTachesFiltrees] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);

  useEffect(() => {
    let filtered = tasks;
    if (filtreNom) {
      filtered = filtered.filter((t) =>
        (t.nom || '').toLowerCase().includes(filtreNom.toLowerCase())
      );
    }
    if (filtreId) {
      filtered = filtered.filter((t) =>
        (t.id || '').toString().includes(filtreId)
      );
    }
    setTachesFiltrees(filtered);
  }, [tasks, filtreNom, filtreId]);

  const handleEdit = (e, tache) => {
    e.stopPropagation();
    if (typeof editerTache === 'function') editerTache(tache);
  };

  const handleDelete = (e, tache) => {
    e.stopPropagation();
    if (typeof supprimerTache === 'function') {
      supprimerTache(tache);
    }
  };

  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      if (newSelection.length > 0) {
        const selectedRow = tachesFiltrees.find(
          (row) => row.task_id === newSelection[0]
        );
        if (selectedRow && typeof taskSelectionnee === 'function') {
          taskSelectionnee(selectedRow);
        }
      }
    },
    [tachesFiltrees, taskSelectionnee]
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDayName = (dayNum) => {
    const days = {
      0: 'Dim',
      1: 'Lun',
      2: 'Mar',
      3: 'Mer',
      4: 'Jeu',
      5: 'Ven',
      6: 'Sam',
    };
    return days[dayNum] || dayNum;
  };

  const getFrequencyDisplay = (row) => {
    if (row.type === 'manual') {
      return 'Manuel';
    }

    if (row.type === 'planned') {
      // Pour planned: afficher la date de lancement (CRON_start_day)
      return row.cron_start_day ? formatDate(row.cron_start_day) : '-';
    }

    if (row.type === 'scheduled') {
      // Vérifier si c'est une fréquence basée sur les jours de la semaine
      if (row.cron_day_of_week && row.cron_day_of_week !== '*') {
        const days = row.cron_day_of_week
          .split(',')
          .map((d) => getDayName(d.trim()));
        const time =
          row.cron_hour !== '*' && row.cron_minute !== '*'
            ? ` à ${row.cron_hour}:${row.cron_minute.padStart(2, '0')}`
            : '';
        return `${days.join(', ')}${time}`;
      }

      // Pour scheduled: construire l'affichage de la fréquence (intervalle de répétition)
      const parts = [];

      const year = parseInt(row.cron_year) || 0;
      const month = parseInt(row.cron_month) || 0;
      const week = parseInt(row.cron_week) || 0;
      const day = parseInt(row.cron_day) || 0;
      const hour = parseInt(row.cron_hour) || 0;
      const minute = parseInt(row.cron_minute) || 0;
      const second = parseInt(row.cron_second) || 0;

      if (year > 0) parts.push(`${year} an${year > 1 ? 's' : ''}`);
      if (month > 0) parts.push(`${month} mois`);
      if (week > 0) parts.push(`${week} semaine${week > 1 ? 's' : ''}`);
      if (day > 0) parts.push(`${day} jour${day > 1 ? 's' : ''}`);
      if (hour > 0) parts.push(`${hour}h`);
      if (minute > 0) parts.push(`${minute}min`);
      if (second > 0) parts.push(`${second}s`);

      return parts.length > 0 ? `Tous les ${parts.join(' ')}` : 'Planifié';
    }

    return '-';
  };

  const rows = useMemo(
    () =>
      (tachesFiltrees || [])
        .filter((row) => row.task_id !== null && row.task_id !== undefined)
        .map((row) => ({
          ...row,
          frequency: getFrequencyDisplay(row),
          actions: (
            <div className={S.actionsContainer}>
              <Bouton
                type={type_btn.secondary}
                rond={true}
                taille="small"
                cliquer={(e) => handleEdit(e, row)}
                ajoutClass={`${S.actionButton} ${S.editButton}`}
              >
                <BiEdit size={14} />
              </Bouton>
              <Bouton
                type={type_btn.danger}
                rond={true}
                taille="small"
                cliquer={(e) => handleDelete(e, row)}
                ajoutClass={S.actionButton}
              >
                <BiTrash size={14} />
              </Bouton>
            </div>
          ),
        })),
    [tachesFiltrees]
  );

  const colonnes = useMemo(
    () =>
      COLUMN_CONFIG.map(([field, label, minWidth]) =>
        createColumn(field, label, minWidth)
      ),
    []
  );

  return (
    <div className={S.conteneurTableau}>
      <div className={S.filtresContainer}>
        <Bouton
          type={type_btn.success}
          cliquer={creerTache}
          ajoutClass={S.boutonCreer}
        >
          <BiPlus size={16} /> Nouvelle tâche
        </Bouton>
      </div>

      <div className={S.tableauContainer}>
        <div className={S.dataGridWrapper}>
          <DataGrid
            rows={rows}
            columns={colonnes}
            getRowId={(row) => row.task_id}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionModelChange}
            disableMultipleRowSelection
            className={S.dataGrid}
          />
        </div>
      </div>

      <div className={S.infoContainer}>
        <span className={S.infoText}>
          Total tâches: {tachesFiltrees.length}
        </span>
      </div>
    </div>
  );
};

export default TableauTasks;
